import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  getAnimationCssVars,
  getAnimationWrapperClassName,
  getSectionAnimation,
} from '../utils/componentAnimation';
import { editorScrollRootRef, previewScrollRootRef } from '../utils/defaults';
import '../styles/webCreatorAnimations.css';

const SectionAnimationContext = createContext(null);

export function useSectionAnimation() {
  return useContext(SectionAnimationContext);
}

function getScrollRoot() {
  if (typeof document === 'undefined') return null;
  return previewScrollRootRef?.current || editorScrollRootRef?.current || window;
}

function getViewportHeight(scrollRoot) {
  if (!scrollRoot || scrollRoot === window) return window.innerHeight || 800;
  return scrollRoot.clientHeight || 800;
}

function sectionAnimSignature(anim) {
  if (!anim?.enabled) return 'off';
  return [
    anim.preset,
    anim.trigger,
    anim.duration,
    anim.delay,
    anim.scrollThreshold,
    anim.scrollOnce,
    anim.scrollOffset,
    anim.parallaxSpeed,
    anim.parallaxDirection,
    anim.staggerEnabled,
    anim.staggerInterval,
  ].join('|');
}

export function SectionAnimationProvider({
  section,
  children,
  disabled = false,
  previewMode = false,
  previewKey = 0,
  forceActive = false,
}) {
  const sectionAnim = getSectionAnimation(section);
  const signature = sectionAnimSignature(sectionAnim);
  const sectionRef = useRef(null);
  const activeComponentsRef = useRef(new Set());
  const hasActivatedRef = useRef(false);
  const [sectionActive, setSectionActive] = useState(false);
  const [parallaxY, setParallaxY] = useState(0);
  const [activationTick, setActivationTick] = useState(0);

  const registerComponentActive = useCallback((componentId) => {
    if (!componentId || activeComponentsRef.current.has(componentId)) return;
    activeComponentsRef.current.add(componentId);
    setActivationTick((n) => n + 1);
  }, []);

  const isComponentActive = useCallback(
    (componentId) => activeComponentsRef.current.has(componentId),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tick forces re-read of the Set
    [activationTick],
  );

  // Reset only when section animation config changes — not when child components activate.
  useEffect(() => {
    activeComponentsRef.current = new Set();
    hasActivatedRef.current = false;
    setSectionActive(false);
    setParallaxY(0);
    setActivationTick((n) => n + 1);
  }, [section?.id, signature, previewKey, forceActive, disabled]);

  useEffect(() => {
    if (!sectionAnim.enabled || disabled) {
      setSectionActive(false);
      return undefined;
    }

    if (previewMode || forceActive) {
      if (sectionAnim.trigger === 'scrollContinuous') {
        setSectionActive(true);
        setParallaxY(sectionAnim.parallaxDirection === 'down' ? 16 : -16);
        return undefined;
      }
      const timer = window.setTimeout(() => {
        hasActivatedRef.current = true;
        setSectionActive(true);
      }, 40);
      return () => window.clearTimeout(timer);
    }

    const el = sectionRef.current;
    if (!el) return undefined;

    if (sectionAnim.trigger === 'load') {
      const timer = window.setTimeout(() => {
        hasActivatedRef.current = true;
        setSectionActive(true);
      }, 0);
      return () => window.clearTimeout(timer);
    }

    if (sectionAnim.trigger === 'scroll' || sectionAnim.trigger === 'scrollOut') {
      const scrollRoot = getScrollRoot();
      const threshold = Math.max(0, Math.min(1, sectionAnim.scrollThreshold));
      let disconnected = false;

      const observer = new IntersectionObserver(
        (entries) => {
          if (disconnected) return;
          const entry = entries[0];
          if (!entry) return;
          const isVisible = entry.isIntersecting && entry.intersectionRatio >= threshold;
          if (sectionAnim.trigger === 'scroll') {
            if (isVisible) {
              hasActivatedRef.current = true;
              setSectionActive(true);
              if (sectionAnim.scrollOnce !== false) {
                disconnected = true;
                observer.disconnect();
              }
            } else if (!sectionAnim.scrollOnce && hasActivatedRef.current) {
              setSectionActive(false);
            }
          } else if (sectionAnim.trigger === 'scrollOut') {
            if (!isVisible) setSectionActive(true);
            else setSectionActive(false);
          }
        },
        {
          root: scrollRoot === window ? null : scrollRoot,
          threshold: [0, threshold, 0.5, 1],
          rootMargin: `${sectionAnim.scrollOffset}px 0px ${sectionAnim.scrollOffset}px 0px`,
        },
      );
      observer.observe(el);
      return () => {
        disconnected = true;
        observer.disconnect();
      };
    }

    if (sectionAnim.trigger === 'scrollContinuous') {
      const scrollRoot = getScrollRoot();
      let frame = null;
      const updateParallax = () => {
        frame = null;
        const node = sectionRef.current;
        if (!node) return;
        const rect = node.getBoundingClientRect();
        const vh = getViewportHeight(scrollRoot);
        const centerOffset = rect.top + rect.height / 2 - vh / 2;
        const factor = sectionAnim.parallaxSpeed * 0.6;
        const delta = centerOffset * factor * (sectionAnim.parallaxDirection === 'down' ? 1 : -1);
        setParallaxY(delta);
        setSectionActive(true);
      };
      const onScroll = () => {
        if (frame != null) return;
        frame = window.requestAnimationFrame(updateParallax);
      };
      updateParallax();
      if (scrollRoot === window) {
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
      } else {
        scrollRoot.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll);
      }
      return () => {
        if (frame != null) window.cancelAnimationFrame(frame);
        if (scrollRoot === window) {
          window.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        } else {
          scrollRoot.removeEventListener('scroll', onScroll);
          window.removeEventListener('resize', onScroll);
        }
      };
    }

    return undefined;
  }, [sectionAnim, signature, disabled, previewMode, forceActive, previewKey]);

  const value = useMemo(
    () => ({
      section,
      sectionAnim,
      sectionActive: sectionActive || forceActive,
      registerComponentActive,
      isComponentActive,
      activationTick,
    }),
    [
      section,
      sectionAnim,
      sectionActive,
      forceActive,
      registerComponentActive,
      isComponentActive,
      activationTick,
    ],
  );

  const wrapSection = sectionAnim.enabled && !disabled;
  const containerClass = wrapSection
    ? getAnimationWrapperClassName(sectionAnim, {
        active: sectionActive || forceActive,
        pending: sectionAnim.trigger !== 'scrollOut' && !(sectionActive || forceActive),
      })
    : '';

  const containerStyle = wrapSection
    ? {
        ...getAnimationCssVars(sectionAnim),
        ...(sectionAnim.trigger === 'scrollContinuous' && (sectionActive || forceActive)
          ? { transform: `translate3d(0, ${parallaxY}px, 0)` }
          : {}),
      }
    : undefined;

  return (
    <SectionAnimationContext.Provider value={value}>
      <div ref={sectionRef} className={containerClass || undefined} style={containerStyle}>
        {children}
      </div>
    </SectionAnimationContext.Provider>
  );
}

export default SectionAnimationContext;
